/**
 * Desktop title-bar drag — which parts of a bar move the window.
 *
 * RESPONSIBILITY
 *   Pure. Given a bar element, list the elements that are the bar's own empty
 *   surface: the bar itself, plus every plain layout element under it reached
 *   without crossing a control or another component. The directive tags those
 *   with Tauri's bare `data-tauri-drag-region`.
 *
 * AI CONTEXT
 *   On macOS the window uses `titleBarStyle: "Overlay"`, so the webview covers
 *   the native title bar and nothing drags unless the page says so. Tauri's
 *   injected script (tauri/src/window/scripts/drag.js) starts a drag on
 *   mousedown and zooms on a double-click when the event's path reaches an
 *   element carrying `data-tauri-drag-region`. A BARE attribute only fires
 *   when that element is the click's own target, never a descendant, so a
 *   tagged layout box can never steal a press from a button, a chip or a menu
 *   inside it. That is why every empty box is tagged individually rather than
 *   the whole bar with `="deep"`: `deep` would also drag from the shell bar's
 *   menu scrim and the padding of an open menu.
 *
 *   WebKit ignores CSS `-webkit-app-region`; the attribute is the only drag
 *   mechanism the macOS webview has.
 */

export const DRAG_REGION_ATTR = 'data-tauri-drag-region';

const INTERACTIVE_TAGS = new Set([
  'A',
  'AUDIO',
  'BUTTON',
  'DETAILS',
  'IFRAME',
  'INPUT',
  'LABEL',
  'NAV',
  'OPTION',
  'SELECT',
  'SUMMARY',
  'TEXTAREA',
  'VIDEO',
]);

const INTERACTIVE_ROLES = new Set([
  'button',
  'checkbox',
  'combobox',
  'dialog',
  'grid',
  'link',
  'listbox',
  'menu',
  'menubar',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'navigation',
  'option',
  'radio',
  'radiogroup',
  'scrollbar',
  'search',
  'searchbox',
  'slider',
  'spinbutton',
  'switch',
  'tab',
  'tablist',
  'textbox',
  'toolbar',
  'tree',
]);

/**
 * A control, or a group of controls (a `nav` capsule, a toolbar, a tab list):
 * pressing it, or the padding between its controls, must never move the window.
 */
export function isInteractiveElement(el: Element): boolean {
  if (INTERACTIVE_TAGS.has(el.tagName)) return true;
  const role = el.getAttribute('role');
  if (role && INTERACTIVE_ROLES.has(role)) return true;
  const editable = el.getAttribute('contenteditable');
  if (editable !== null && editable !== 'false') return true;
  const tabindex = el.getAttribute('tabindex');
  if (tabindex !== null && tabindex !== '-1') return true;
  return el.hasAttribute('popover');
}

/**
 * The bar's own empty surfaces, `bar` first. A child is skipped, with
 * everything under it, when it is interactive, when it is another component
 * (a custom element owns its own hit areas), when it opts out with
 * `data-tauri-drag-region="false"`, or when it is not an HTML element (SVG
 * parts never match Tauri's `instanceof HTMLElement` walk).
 */
export function dragSurfaces(bar: Element): HTMLElement[] {
  const view = bar.ownerDocument?.defaultView;
  const HTMLElementCtor = view?.HTMLElement;
  if (!HTMLElementCtor || !(bar instanceof HTMLElementCtor)) return [];
  const out: HTMLElement[] = [bar];
  const walk = (parent: Element): void => {
    for (const child of Array.from(parent.children)) {
      if (!(child instanceof HTMLElementCtor)) continue;
      if (isInteractiveElement(child)) continue;
      if (child.tagName.includes('-')) continue;
      if (child.getAttribute(DRAG_REGION_ATTR) === 'false') continue;
      out.push(child);
      walk(child);
    }
  };
  walk(bar);
  return out;
}
